/**
 * Tiny offline-first cache for student-self GET endpoints.
 *
 * Why: students hit /api/student-self repeatedly (dashboard ↔ activity
 * ↔ plan-detail). Even with the 30s server Cache-Control, every cold
 * tab still pays a round-trip. Persisting the last response in
 * IndexedDB lets us paint the UI from disk *immediately*, then refresh
 * in the background — the canonical stale-while-revalidate pattern.
 *
 * Scope: GETs only. POST/PATCH go straight to the network so write
 * semantics aren't surprising.
 *
 * Key shape: `${studentId}::${path}` so two students sharing a device
 * don't see each other's cache. studentId is read from the JWT.
 */

import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'fb-student-cache';
const DB_VERSION = 1;
const STORE = 'responses';

interface CacheRow {
  key: string;
  data: unknown;
  fetchedAt: number;
  // ETag from the server, when present. Lets the next refresh send
  // If-None-Match and short-circuit at 304 if nothing's changed.
  etag?: string;
}

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB(): Promise<IDBPDatabase> {
  if (typeof window === 'undefined' || !('indexedDB' in window)) {
    return Promise.reject(new Error('IndexedDB unavailable'));
  }
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

/** Decode the JWT payload — best-effort, no signature check (the
 *  server validates that). Fallback identity when the stored user
 *  object isn't available yet. */
function studentIdFromToken(): string {
  try {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    if (!token) return 'anon';
    const payload = token.split('.')[1];
    if (!payload) return 'anon';
    const padded = payload + '==='.slice((payload.length + 3) % 4);
    const claims = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
    return String(claims.studentId ?? claims.userId ?? 'anon');
  } catch {
    return 'anon';
  }
}

/**
 * Namespace the cache by the *signed-in identity*, so two accounts on the
 * same device never see each other's data. We read the stored user object
 * first (it reliably carries rollNumber for students and id/email for staff)
 * and only fall back to the JWT claim. Stable + always present > decoding a
 * token whose claim names can drift. Lowercased so 'A1B2'/'a1b2' don't split.
 */
function userScope(): string {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('auth_user') : null;
    if (raw) {
      const u = JSON.parse(raw);
      // Prefer the human-unique handle that actually exists: roll number for
      // students, email for staff — then fall back to ids.
      const id = u?.rollNumber ?? u?.email ?? u?.studentId ?? u?.id ?? u?.username;
      if (id) return String(id).toLowerCase();
    }
  } catch {
    /* fall through to the token */
  }
  return studentIdFromToken();
}

function cacheKey(path: string): string {
  return `${userScope()}::${path}`;
}

export async function readCache<T>(path: string): Promise<{ data: T; fetchedAt: number; etag?: string } | null> {
  try {
    const db = await getDB();
    const row = (await db.get(STORE, cacheKey(path))) as CacheRow | undefined;
    if (!row) return null;
    return { data: row.data as T, fetchedAt: row.fetchedAt, etag: row.etag };
  } catch {
    return null;
  }
}

export async function writeCache<T>(path: string, data: T, etag?: string): Promise<void> {
  try {
    const db = await getDB();
    const row: CacheRow = { key: cacheKey(path), data, fetchedAt: Date.now(), etag };
    await db.put(STORE, row);
  } catch {
    // Quota exceeded / private browsing — don't break the app.
  }
}

export async function clearCache(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear(STORE);
  } catch {
    // Ignore — first-time visitors have nothing to clear.
  }
}

/** Wipe just one entry. Use after a write that invalidates a
 *  specific endpoint (e.g. marking a plan item done invalidates
 *  both the plan detail and the dashboard counts). */
export async function invalidateCache(path: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete(STORE, cacheKey(path));
  } catch {
    // Ignore.
  }
}
